import {
  toWSDate,
  WSGrid,
  WSPillProps,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BackgroundCheckConfiguration,
  BackgroundCheckPackage,
  DocumentTemplate,
  InsuranceCoverageSource,
  InsuranceMonitorConfiguration,
  RequirementDefinition,
  RequirementType,
  SharedFileRequestConfiguration
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";

import {
  FieldView,
  FieldViewProps
} from "../../../../shared/components/FieldView/FieldView";
import { RequirementInstructionsView } from "../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../../Requirements/modules/IdentityVerification/instructions";
import { selectorIsAcceptedSourceTypeIncluded } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorIsAcceptedSourceTypeIncluded";
import { selectorPolicyTypesFromSettings } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";
import { mimeTypesToLabel } from "../../modules/SharedFile/createRequirementDefinition/utils";
import { InsuranceInstructionsView } from "../../routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { getRequirementDefinitionTemplateByParams } from "../../utils/getRequirementDefinitionTemplateByParams";

type Props = {
  requirementDefinition: RequirementDefinition;
  singedDocumentTemplate?: DocumentTemplate;
  sharedFileSettings?: SharedFileRequestConfiguration;
  backgroundChecksSettings?: BackgroundCheckConfiguration;
  // v1 embedded the package on the settings response; on v3 the configuration
  // carries only `packageId`, so the package is passed in alongside it.
  backgroundCheckPackage?: BackgroundCheckPackage;
  monitorSettings?: InsuranceMonitorConfiguration;
};

export const RequirementInformation: React.FC<Props> = ({
  requirementDefinition,
  singedDocumentTemplate,
  sharedFileSettings,
  backgroundChecksSettings,
  backgroundCheckPackage,
  monitorSettings
}) => {
  const fields = useMemo(() => {
    const fieldsByType: Partial<Record<RequirementType, FieldViewProps[]>> = {
      [RequirementType.SIGNATURE]: [
        {
          label: "Required signatures",
          value:
            singedDocumentTemplate?.signerRoles?.length === 2
              ? "Both contractors and client signatures required"
              : "Contractor signature required"
        }
      ],
      [RequirementType.DOCUMENT_UPLOAD]: [
        {
          label: "File types",
          value: mimeTypesToLabel(sharedFileSettings?.acceptedMimeTypes || [])
        },
        {
          fullWidth: true,
          label: "Instructions",
          value: sharedFileSettings?.instructions ? (
            <RequirementInstructionsView
              instructions={sharedFileSettings.instructions}
            />
          ) : undefined
        }
      ],
      [RequirementType.BACKGROUND_CHECK]: [
        {
          label: "Package",
          value: backgroundCheckPackage?.title
        },
        {
          label: "Name",
          value: backgroundChecksSettings?.title
        },
        {
          fullWidth: true,
          label: "Instructions",
          value: backgroundChecksSettings?.instructions ? (
            <RequirementInstructionsView
              instructions={backgroundChecksSettings.instructions}
            />
          ) : undefined
        }
      ],
      [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: [
        {
          fullWidth: true,
          label: "Instructions",
          value: (
            <RequirementInstructionsView
              instructions={IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS}
            />
          )
        }
      ],
      [RequirementType.INSURANCE_COVERAGE]: [
        {
          fullWidth: true,
          label: "Instructions",
          value: (
            <InsuranceInstructionsView
              allowMasterPolicy={selectorIsAcceptedSourceTypeIncluded({
                acceptedSourceType: InsuranceCoverageSource.PLAN_ENROLLMENT,
                insuranceMonitorConfiguration: monitorSettings
              })}
              insuranceName={
                getRequirementDefinitionTemplateByParams({
                  policyType: selectorPolicyTypesFromSettings(monitorSettings),
                  type: RequirementType.INSURANCE_COVERAGE
                }).name
              }
              policyTypes={selectorPolicyTypesFromSettings(monitorSettings)}
            />
          )
        }
      ],
      [RequirementType.REGISTRATION]: [],
      [RequirementType.TAX_VERIFICATION]: [],
      [RequirementType.PAYOUT_METHOD]: []
    };

    const result: FieldViewProps[] = [
      {
        label: "Creation date",
        value: toWSDate(requirementDefinition.events?.createdAt).formattedValue
      },
      {
        label: "Expiration",
        value: requirementDefinition.expirationDays
          ? `Expire in ${requirementDefinition.expirationDays} days`
          : "Never expires"
      },
      {
        fullWidth: true,
        label: "Description",
        value: (
          <WSText.ParagraphSm as="pre">
            {requirementDefinition.description}
          </WSText.ParagraphSm>
        )
      },
      {
        label: "Manual renewal allowed",
        value: requirementDefinition.manualRenewalAllowed ? "Yes" : "No"
      },
      {
        label: "Version",
        value: `v${requirementDefinition.version}`
      },
      {
        fullWidth: true,
        label: "Internal note",
        pill: {
          icon: "lock",
          text: "Private",
          theme: "neutral"
        } as WSPillProps,
        value: requirementDefinition.versionNote
      },
      // `fieldsByType` only lists the types with extra fields; types without an
      // entry (e.g. EmployeeTaxForms, a wire value the installed SDK enum
      // predates) fall back to [] — spreading `undefined` throws.
      ...(fieldsByType[requirementDefinition.type!] ?? [])
    ].filter(i => i.value);

    return result;
  }, [
    singedDocumentTemplate,
    sharedFileSettings,
    requirementDefinition,
    backgroundChecksSettings,
    backgroundCheckPackage?.title,
    monitorSettings
  ]);

  return (
    <WSGrid gutter="2XL">
      {fields.map(({ fullWidth, label, ...fieldProps }) => (
        <WSGrid.Item
          key={label}
          span={fullWidth ? { xs: "12" } : { l: "6", s: "6", xs: "12" }}
        >
          <FieldView label={label} {...fieldProps} />
        </WSGrid.Item>
      ))}
    </WSGrid>
  );
};
